import React, { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { yup } from '@reapitform/utils'
import { ButtonGroup, Checkbox, Drawer, InputGroup, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { useAdminEnums } from '../../../../api/enums'
import { AdminRecipientCard, EnumOption } from '../../../../api/types'
import { useCreateAdminRecipientCard, useUpdateAdminRecipientCard } from '../../../../api/recipient-cards'
import { FieldHelp, FieldStack } from '../../../../components/form-details-fields/style'
import { TYPED_RECIPIENT_SECTION_TYPES } from '../recipient-section-types'

// Explicit "None" option — a None card holds manually-added dynamic fields (no component factory). Its value
// matches the backend SectionType.None member; the label is UI-only. Mirrors the review-card section drawer.
const NONE_SECTION_TYPE: EnumOption = { value: 'None', label: 'None – dynamic fields' }

interface RecipientCardValues {
  displayName: string
  icon: string
  sectionType: string
  order: number
  allowMultiple: boolean
  excludedDocumentType: string | null
}

const recipientCardSchema = yup.object({
  displayName: yup.string().required('Enter a display name'),
  icon: yup.string().optional().default(''),
  sectionType: yup.string().optional().default('None'),
  order: yup.number().typeError('Enter a valid number').min(0, 'Order must be 0 or more').required('Enter an order'),
  allowMultiple: yup.boolean().default(false),
  excludedDocumentType: yup.string().nullable().default(null),
})

interface RecipientCardDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  recipientStepReferenceID: string
  /** The card being edited, or null to create a new one. */
  card: AdminRecipientCard | null
  /** All existing cards on the step — used to prevent duplicate order values. */
  existingCards: AdminRecipientCard[]
  /** Default order for a new card (appended to the end). */
  nextOrder: number
  onSaved: () => void
}

/** Add/Edit drawer for a recipient card. Creates (POST) or updates (PUT) then triggers a builder refresh. */
export const RecipientCardDrawer: React.FC<RecipientCardDrawerProps> = ({
  isOpen,
  onOpenChange,
  recipientStepReferenceID,
  card,
  existingCards,
  nextOrder,
  onSaved,
}) => {
  const formId = 'recipient-card-form'
  const { data: enums } = useAdminEnums()
  const [createCard, creating] = useCreateAdminRecipientCard(recipientStepReferenceID)
  const [updateCard, updating] = useUpdateAdminRecipientCard(recipientStepReferenceID, card?.referenceID ?? '')
  const isBusy = creating || updating

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    reset,
    formState: { errors },
  } = useForm<RecipientCardValues>({
    resolver: yupResolver(recipientCardSchema),
    defaultValues: {
      displayName: '',
      icon: '',
      sectionType: 'None',
      order: nextOrder,
      allowMultiple: false,
      excludedDocumentType: null,
    },
  })

  const [iconOption, setIconOption] = useState<EnumOption | null>(null)
  const [sectionTypeOption, setSectionTypeOption] = useState<EnumOption>(NONE_SECTION_TYPE)
  const [excludedDocumentTypeOption, setExcludedDocumentTypeOption] = useState<EnumOption | null>(null)

  // "None – dynamic fields" first, then only the SectionTypes RecipientCardModelBuilder actually has a
  // dedicated component for. The full enum catalog includes types with no recipient-side handling at all
  // (e.g. StandardTerm, Document, Link, the *Signature types — those are review-card-only concepts) — offering
  // them here would silently behave exactly like None while claiming to "generate fields automatically".
  // If this card already has one of those unsupported types set (from before this filter existed), it's kept
  // as an extra option so editing shows its real value instead of misrepresenting it as None.
  const sectionTypeOptions = useMemo<EnumOption[]>(() => {
    const typed = (enums?.SectionType ?? []).filter((option) => TYPED_RECIPIENT_SECTION_TYPES.has(option.value))
    const currentUntyped =
      card?.sectionType && card.sectionType !== 'None' && !typed.some((option) => option.value === card.sectionType)
        ? enums?.SectionType?.find((option) => option.value === card.sectionType)
        : undefined
    return [NONE_SECTION_TYPE, ...typed, ...(currentUntyped ? [currentUntyped] : [])]
  }, [enums, card])

  // Reset the form to the card's values (or blank create defaults) each time the drawer opens.
  useEffect(() => {
    if (!isOpen) return
    reset({
      displayName: card?.displayName ?? '',
      icon: card?.icon ?? '',
      sectionType: card?.sectionType || 'None',
      order: card?.order ?? nextOrder,
      allowMultiple: card?.allowMultiple ?? false,
      excludedDocumentType: card?.excludedDocumentType ?? null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, card])

  useEffect(() => {
    if (!isOpen) return
    setIconOption(card?.icon ? (enums?.Icon?.find((option) => option.value === card.icon) ?? null) : null)
    const sectionTypeValue = card?.sectionType || 'None'
    setSectionTypeOption(sectionTypeOptions.find((option) => option.value === sectionTypeValue) ?? NONE_SECTION_TYPE)
    setExcludedDocumentTypeOption(
      card?.excludedDocumentType
        ? (enums?.DocumentType?.find((option) => option.value === card.excludedDocumentType) ?? null)
        : null,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, card, enums, sectionTypeOptions])

  const handleClose = () => onOpenChange(false)

  const onSubmit: SubmitHandler<RecipientCardValues> = async (values) => {
    // Order must be unique across the step's cards (excluding the card being edited).
    const takenOrders = existingCards
      .filter((existing) => existing.referenceID !== card?.referenceID)
      .map((existing) => existing.order)
    if (takenOrders.includes(values.order)) {
      setError('order', {
        type: 'manual',
        message: `A recipient card already exists with order ${values.order}. Choose a different order.`,
      })
      return
    }

    try {
      if (card) await updateCard(values)
      else await createCard(values)
      onSaved()
      onOpenChange(false)
    } catch {
      // Errors surfaced via the hooks' toasts.
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer
      isOpen={isOpen}
      title={card ? 'Edit Recipient Card' : 'Add Recipient Card'}
      onClose={handleClose}
      footerItems={footer}
    >
      {isOpen && (
        <FieldStack id={formId} onSubmit={handleSubmit(onSubmit)}>
          <InputGroup
            label="Display Name *"
            placeholder="e.g. Tenant"
            {...register('displayName')}
            errorMessage={errors.displayName?.message}
          />
          <Select
            label="Icon"
            isClearable
            options={enums?.Icon ?? []}
            value={iconOption}
            getOptionLabel={(option: EnumOption) => option.label}
            getOptionValue={(option: EnumOption) => option.value}
            onChange={(option: EnumOption | null) => {
              setIconOption(option)
              setValue('icon', option?.value ?? '')
            }}
            errorMessage={errors.icon?.message}
          />
          <div>
            <Select
              label="Section Type"
              options={sectionTypeOptions}
              value={sectionTypeOption}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => {
                const next = option ?? NONE_SECTION_TYPE
                setSectionTypeOption(next)
                setValue('sectionType', next.value)
              }}
              errorMessage={errors.sectionType?.message}
            />
            <FieldHelp>
              A section type is built by a dedicated component that generates its fields automatically (e.g. Person
              Recipient, Property Address). Choose “None – dynamic fields” to add your own fields to the card instead.
            </FieldHelp>
          </div>
          <InputGroup
            label="Order *"
            inputMode="numeric"
            {...register('order', { valueAsNumber: true })}
            errorMessage={errors.order?.message}
          />
          <Checkbox
            label="Allow Multiple"
            supplementaryInfo="Used for collection cards that repeat once per item — e.g. Pets and Insurance in the current forms — so the recipient sees one instance of the card per entry."
            checked={Boolean(watch('allowMultiple'))}
            onChange={() => setValue('allowMultiple', !watch('allowMultiple'))}
          />
          <div>
            <Select
              label="Excluded Document Type"
              isClearable
              options={enums?.DocumentType ?? []}
              value={excludedDocumentTypeOption}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => {
                setExcludedDocumentTypeOption(option)
                setValue('excludedDocumentType', option?.value ?? null)
              }}
              errorMessage={(errors.excludedDocumentType as { message?: string })?.message}
            />
            <FieldHelp>Hides this card for the selected document type. Leave blank to show it for all types.</FieldHelp>
          </div>
        </FieldStack>
      )}
    </Drawer>
  )
}
